/**
 * Error-page category mapping (ALL-4857).
 *
 * Canonical, framework-agnostic source of truth for how the ~76
 * `AuthReasonCode` values (see codegen/auth-reason-codes.json, generated by
 * scripts/generate-auth-reason-codes.mjs) and generic HTTP failures collapse
 * into a small set of UI categories. Each category has one localized
 * title/message/primary-action per supported locale (packages/design-system
 * /error-page/locales/*.json) so we do not need to hand-translate 76 * 10
 * sentences — only ~10 categories * 10 locales.
 *
 * This file has no React/DOM dependency so it can be copied verbatim into
 * every app (sandbox-shell, enterprise-wallet, admin-portal) regardless of
 * whether that app is a React SPA, a hash-routed vanilla-JS SPA, or a static
 * page bundle. Each app's copy lives at src/i18n/error-categories.(ts|mjs)
 * and MUST stay byte-identical to this file (enforced by
 * scripts/verify-error-page-sync.mjs).
 */

export type ErrorCategory =
  | "auth_required"
  | "session_ended"
  | "step_up_required"
  | "permission_denied"
  | "approval_required"
  | "invite_expired"
  | "invite_required"
  | "rate_limited"
  | "service_unavailable"
  | "bad_request"
  | "not_found"
  | "generic_4xx"
  | "generic_5xx"
  | "unknown_client_error";

export type PrimaryActionKind = "retry" | "sign_in_again" | "contact_support" | "go_home";

/** Explicit reason-code -> category overrides. Anything not listed here
 * falls back to a category chosen from its httpStatus (see
 * `categoryForHttpStatus`), so a newly-added AuthReasonCode is never
 * unhandled — it just gets a generic-but-correct category until someone
 * adds a precise override. */
export const REASON_CODE_CATEGORY_OVERRIDES: Record<string, ErrorCategory> = {
  AUTH_MISSING_CREDENTIAL: "auth_required",
  AUTH_MALFORMED_CREDENTIAL: "auth_required",
  AUTH_EXPIRED_CREDENTIAL: "auth_required",
  AUTH_REVOKED_CREDENTIAL: "auth_required",
  AUTH_UNKNOWN_CREDENTIAL: "auth_required",
  AUTH_CONTEXT_INVALID: "auth_required",
  AUTH_CLIENT_ASSERTION_INVALID: "auth_required",

  SESSION_LOGGED_OUT: "session_ended",
  SESSION_EXPIRED: "session_ended",
  SESSION_REVOKED: "session_ended",
  AUTH_STATE_MISMATCH: "session_ended",
  AUTH_NONCE_MISMATCH: "session_ended",
  AUTH_CODE_REPLAYED: "session_ended",
  AUTH_CALLBACK_DENIED: "session_ended",

  AUTH_STEP_UP_REQUIRED: "step_up_required",
  AUTH_STEP_UP_EXPIRED: "step_up_required",
  AUTH_STEP_UP_INVALID: "step_up_required",
  AUTH_STEP_UP_ACTION_MISMATCH: "step_up_required",
  AUTH_STEP_UP_RESOURCE_MISMATCH: "step_up_required",
  AUTH_STEP_UP_REPLAY: "step_up_required",
  AUTH_STEP_UP_CONSUMED: "step_up_required",
  AUTH_STEP_UP_REVOKED: "step_up_required",
  AUTH_ASSURANCE_INSUFFICIENT: "step_up_required",
  AUTH_ASSURANCE_NOT_ALLOWED: "step_up_required",
  MFA_STRENGTH_INSUFFICIENT: "step_up_required",

  AUTH_TENANT_MISMATCH: "permission_denied",
  AUTH_ENVIRONMENT_MISMATCH: "permission_denied",
  AUTH_TRUST_DOMAIN_MISMATCH: "permission_denied",
  AUTH_INSUFFICIENT_SCOPE: "permission_denied",
  AUTH_INSUFFICIENT_PERMISSION: "permission_denied",
  AUTH_ROLE_NOT_ALLOWED: "permission_denied",
  AUTH_RESOURCE_OWNERSHIP_MISMATCH: "permission_denied",
  AUTH_INTERNAL_CALLER_MISMATCH: "permission_denied",
  MESH_DIRECT_CALL_UNAUTHORIZED: "permission_denied",
  AUTH_CAPABILITY_ROUTE_DENIED: "permission_denied",
  AUTH_SERVICE_SCOPE_DENIED: "permission_denied",
  AUTH_AGENT_GRANT_DENIED: "permission_denied",
  AUTH_POLICY_DENIED: "permission_denied",
  AUTH_GATEWAY_ALLOWED_SERVICE_DENIED: "permission_denied",
  AUTH_MTLS_BINDING_MISMATCH: "permission_denied",
  AUTH_EMBEDDED_WEBVIEW_DISALLOWED: "permission_denied",
  AUTH_MACHINE_WALLET_QUOTA_EXCEEDED: "permission_denied",
  AUTH_MACHINE_CHAIN_NOT_ALLOWED: "permission_denied",
  AUTH_MACHINE_PROFILE_NOT_ALLOWED: "permission_denied",
  AUTH_MACHINE_PRINCIPAL_CANNOT_APPROVE: "permission_denied",
  AUTH_ACTING_PRINCIPAL_INVALID: "permission_denied",
  AUTH_ACTING_PRINCIPAL_UNTRUSTED_CALLER: "permission_denied",
  SELF_ESCALATION_DENIED: "permission_denied",
  BOOTSTRAP_ADMIN_SCOPE_VIOLATION: "permission_denied",
  AGENT_BOOTSTRAP_NOT_ALLOWED: "permission_denied",
  SEPARATION_OF_DUTIES_VIOLATION: "permission_denied",
  USER_DEACTIVATE_ONLY: "permission_denied",

  AUTH_POLICY_REQUIRED: "approval_required",
  AUTH_APPROVAL_REQUIRED: "approval_required",
  AUTH_SERVICE_FLOOR_WEAKENED: "approval_required",
  ADMIN_QUORUM_NOT_SATISFIED: "approval_required",
  AGENT_CREATION_REQUIRES_ADMIN_QUORUM: "approval_required",
  AGENT_OWNER_REQUIRED: "approval_required",
  AGENT_CREDENTIAL_ROTATION_REQUIRED: "approval_required",
  SUPPORT_ACCESS_REQUIRES_CUSTOMER_APPROVAL: "approval_required",
  LAST_ADMIN_PROTECTION: "approval_required",
  DOWNSTREAM_CONTROL_BLOCKED: "approval_required",
  AUTH_REPLAY_DETECTED: "approval_required",
  AUTHORIZATION_SCOPE_CONFLICT: "approval_required",
  BOOTSTRAP_MANIFEST_SECRET_MATERIAL: "approval_required",
  BOOTSTRAP_IDENTITY_DECISION_REQUIRED: "approval_required",

  BOOTSTRAP_INVITE_EXPIRED: "invite_expired",
  BOOTSTRAP_ROLE_EXPIRED: "invite_expired",
  DOMAIN_NOT_VERIFIED: "invite_expired",

  AUTH_INVITE_REQUIRED: "invite_required",

  AUTH_RATE_LIMITED: "rate_limited",

  AUTH_POLICY_UNAVAILABLE: "service_unavailable",
  AUTH_ADMISSION_CONTROL_UNAVAILABLE: "service_unavailable",
  READINESS_UNAVAILABLE: "service_unavailable",
  AUTH_PROVIDER_UNAVAILABLE: "service_unavailable",
  DIAGNOSTIC_SINK_UNAVAILABLE: "service_unavailable",

  BULK_INVITE_LIMIT_EXCEEDED: "bad_request",
};

export function categoryForHttpStatus(httpStatus: number): ErrorCategory {
  if (httpStatus === 401) return "auth_required";
  if (httpStatus === 403) return "permission_denied";
  if (httpStatus === 404) return "not_found";
  if (httpStatus === 409) return "approval_required";
  if (httpStatus === 429) return "rate_limited";
  if (httpStatus >= 500) return "generic_5xx";
  if (httpStatus >= 400) return "generic_4xx";
  return "generic_4xx";
}

export function categoryForReasonCode(reasonCode: string | undefined | null, httpStatus?: number): ErrorCategory {
  if (reasonCode === "UNKNOWN_CLIENT_ERROR") return "unknown_client_error";
  if (reasonCode && REASON_CODE_CATEGORY_OVERRIDES[reasonCode]) {
    return REASON_CODE_CATEGORY_OVERRIDES[reasonCode];
  }
  if (typeof httpStatus === "number") return categoryForHttpStatus(httpStatus);
  return "generic_4xx";
}

export const PRIMARY_ACTION_BY_CATEGORY: Record<ErrorCategory, PrimaryActionKind> = {
  auth_required: "sign_in_again",
  session_ended: "sign_in_again",
  step_up_required: "sign_in_again",
  permission_denied: "contact_support",
  approval_required: "contact_support",
  invite_expired: "contact_support",
  invite_required: "go_home",
  rate_limited: "retry",
  service_unavailable: "retry",
  bad_request: "go_home",
  not_found: "go_home",
  generic_4xx: "retry",
  generic_5xx: "retry",
  unknown_client_error: "retry",
};
